import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { Layout } from '@eb/eds-layout';
import { withMainControls } from '@eb/eds-structure';
import { withOverlayControls } from '@eb/eds-structure';
import {
    addWindowListener,
    removeWindowListener,
    HAS_DOCUMENT,
} from '@eventbrite/eds-utils';
import {
    HAS_WINDOW_LISTENERS,
    getWindowObject,
    HAS_WINDOW,
} from '@eb/feature-detection';
import { getLocationSuggestions } from '@eb/location-autocomplete';
import { trackEventFromState } from '@eb/site-analytics';

import { useMount } from 'react-use';
import {
    searchLocationSuggestions as searchLocationSuggestionsAction,
    locationChange as locationChangeAction,
    getUserCurrentLocation as getUserCurrentLocationAction,
} from '../../../../redux/actions/location';
import { trackPageView as trackPageViewAction } from '../../../../redux/actions/analytics';

import {
    GA_CITY_BROWSE_CATEGORY,
    GA_SCROLL_DEPTH_ACTION,
    GA_SCROLL_COMPLETE_LABEL,
    GA_SCROLL_NA_LABEL,
} from '../../../../constants/analytics';

import {
    LOCATION_DIALOG_CONTENT,
    ARTIST_SOCIAL_LINKS_PROPTYPE,
    LOCATION_SUGGESTION_PROPTYPE,
} from '../../../../constants';

import { getMaskState } from '../../utils';

import { CityBrowseHeader } from '../../components/CityBrowseHeader';
import { MainContent, MainContentDataLayer } from '../MainContent';
import { CityBrowseFooter } from '../../components/CityBrowseFooter';

const A2H_VISIT = 'addToHomescreenVisit';
const SCROLL_DEBOUNCE_TIME = 200;
const ONLINE_EVENT_BANNER_STYLES = {
    maxWidth: '1276px',
    margin: '0 auto',
    padding: '32px 0',
};

const trackScrollPosition = (trackGAEvent) => {
    if (document.body.scrollHeight <= window.innerHeight + window.scrollY) {
        trackGAEvent({
            category: GA_CITY_BROWSE_CATEGORY,
            label: GA_SCROLL_COMPLETE_LABEL,
            action: GA_SCROLL_DEPTH_ACTION,
        });
    }
};

const trackAddToHomescreenLink = (trackGAEvent) => {
    const matchMedia = getWindowObject('matchMedia');
    const navigator = getWindowObject('navigator');

    // Track when users arrive to city browse through an add to homescreen link
    if (matchMedia && matchMedia('(display-mode: standalone)').matches) {
        trackGAEvent({
            action: A2H_VISIT,
        });
    } else {
        // Tracking in Safari
        // eslint-disable-next-line no-lonely-if
        if (navigator && navigator.standalone === true) {
            trackGAEvent({
                action: A2H_VISIT,
            });
        }
    }
};

const BannerStyleProvider = ({ children }) => {
    // If the banner chooses not to render, don't
    // show the styles
    if (!children) {
        return null;
    }

    return <div style={ONLINE_EVENT_BANNER_STYLES}>{children}</div>;
};

const handleScrollTracking = (trackGAEvent) => {
    if (HAS_WINDOW && HAS_WINDOW_LISTENERS && HAS_DOCUMENT) {
        if (document.body.scrollHeight === window.innerHeight) {
            trackGAEvent({
                category: GA_CITY_BROWSE_CATEGORY,
                label: GA_SCROLL_NA_LABEL,
                action: GA_SCROLL_DEPTH_ACTION,
            });
        }
    }

    const debouncedTrackScrollPosition = debounce(
        trackScrollPosition.bind(null, trackGAEvent),
        SCROLL_DEBOUNCE_TIME,
    );

    addWindowListener('scroll', debouncedTrackScrollPosition);

    return debouncedTrackScrollPosition;
};

const CityBrowseBaseScreen = (props) => {
    const {
        artistImageUrl,
        artistName,
        artistSocialLinks,
        artistSummary,
        cityBlurb,
        currentPlace,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        domain,
        experiments,
        featureFlags,
        footerImageUrl,
        footerSmallImageUrl,
        h1Prefix,
        history,
        handleLocationSelect,
        handleLocationSuggestionChange,
        headerImageUrl,
        headerSmallImageUrl,
        isOnline,
        isWaitingForLocation,
        locationSuggestions,
        middleImageUrl,
        onTrackPageViewAction,
        middleSmallImageUrl,
        locationDenied,
        shouldHideMask,
        trackGAEvent,
        handleUseCurrentLocation,
        isBotRequest,
        showOverlay,
        buckets,
        moreBuckets,
        excludedBuckets,
        placeId,
    } = props;

    useMount(() => {
        onTrackPageViewAction(true);
        trackAddToHomescreenLink(trackGAEvent);
    });

    useEffect(() => {
        const listener = handleScrollTracking(trackGAEvent);

        () => removeWindowListener('scroll', listener);
    }, [trackGAEvent]);

    useEffect(() => {
        if (locationDenied) {
            showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }
    }, [locationDenied, showOverlay]);

    return (
        <Layout>
            <CityBrowseHeader
                isBotRequest={isBotRequest}
                locationSuggestions={locationSuggestions}
                handleLocationSuggestionChange={handleLocationSuggestionChange}
                handleLocationSelect={handleLocationSelect}
                currentPlace={currentPlace}
                cityBlurb={cityBlurb}
                h1Prefix={h1Prefix}
                cityImage={headerImageUrl}
                citySmallImage={headerSmallImageUrl}
                shouldHideMask={shouldHideMask}
                experiments={experiments}
                domain={domain}
                trackGAEvent={trackGAEvent}
                isUseCurrentLocationEnabled={featureFlags.useCurrentLocation}
                handleUseCurrentLocation={() =>
                    handleUseCurrentLocation(history, true)
                }
                isOnline={isOnline}
                isWaitingForLocation={isWaitingForLocation}
                history={props.history}
            />
            <MainContentDataLayer
                placeId={placeId}
                isOnline={isOnline}
                buckets={buckets}
                excludedBuckets={excludedBuckets}
                moreBuckets={moreBuckets}
            >
                <MainContent
                    middleImage={middleImageUrl}
                    middleSmallImage={middleSmallImageUrl}
                    decorationTopLeftUrl={decorationTopLeftUrl}
                    decorationTopRightUrl={decorationTopRightUrl}
                    decorationBottomLeftUrl={decorationBottomLeftUrl}
                    decorationBottomRightUrl={decorationBottomRightUrl}
                    featureFlags={featureFlags}
                />
            </MainContentDataLayer>
            <CityBrowseFooter
                footerImage={footerImageUrl}
                footerSmallImage={footerSmallImageUrl}
                artistImage={artistImageUrl}
                artistSummary={artistSummary}
                artistName={artistName}
                artistSocialLinks={artistSocialLinks}
                experiments={experiments}
            />
        </Layout>
    );
};

CityBrowseBaseScreen.propTypes = {
    domain: PropTypes.string.isRequired,
    /*
     * From withOverlayControls HoC
     */
    showOverlay: PropTypes.func.isRequired,
    /*
     * Tracker
     */
    onTrackPageViewAction: PropTypes.func.isRequired,
    /*
     * Array of location suggestions for consumption by
     * the location switcher
     */
    locationSuggestions: LOCATION_SUGGESTION_PROPTYPE,
    /*
     * Callback to display updated autocomplete suggestions
     */
    handleLocationSuggestionChange: PropTypes.func,
    /*
     * Callback for handling changing of the selected metro
     */
    handleLocationSelect: PropTypes.func,
    /*
     * Brief city description for the selected metro
     */
    cityBlurb: PropTypes.string,
    /*
     * Image to be displayed in the metro's header
     */
    headerImageUrl: PropTypes.string,
    /*
     * Image to be displayed in the metro's header on small viewports
     */
    headerSmallImageUrl: PropTypes.string,
    /*
     * Image to be displayed in the middle section of the page for selected metros
     */
    middleImageUrl: PropTypes.string,
    /*
     * Small Image to be displayed in the middle section of the page on small viewports
     */
    middleSmallImageUrl: PropTypes.string,
    /*
     * Image to be displayed in the footer section of the page for selected metros
     */
    footerImageUrl: PropTypes.string,
    /*
     * Image to be displayed in the footer section of the page on small viewports
     */
    footerSmallImageUrl: PropTypes.string,
    /*
     * The artist's chosen image to represent themselves
     */
    artistImageUrl: PropTypes.string,
    /*
     * Decorative image provided by artist
     */
    decorationTopLeftUrl: PropTypes.string,
    /*
     * Decorative image provided by artist
     */
    decorationTopRightUrl: PropTypes.string,
    /*
     * Decorative image provided by artist
     */
    decorationBottomLeftUrl: PropTypes.string,
    /*
     * Decorative image provided by artist
     */
    decorationBottomRightUrl: PropTypes.string,
    /*
     * Artist summary provided for the artist's attribution
     */
    artistSummary: PropTypes.string,
    /*
     * Artist name
     */
    artistName: PropTypes.string,
    /*
     * Artist social links
     */
    artistSocialLinks: ARTIST_SOCIAL_LINKS_PROPTYPE,
    /*
     * Tracking event from state from @eb/site-analytics
     */
    trackGAEvent: PropTypes.func,
    /*
     * Should hide mask
     */
    shouldHideMask: PropTypes.bool,

    featureFlags: PropTypes.shape({
        useCurrentLocation: PropTypes.bool,
    }),

    handleUseCurrentLocation: PropTypes.func,

    locationDenied: PropTypes.bool,

    currentPlace: PropTypes.string,

    h1Prefix: PropTypes.string,

    isWaitingForLocation: PropTypes.bool,

    isOnline: PropTypes.bool,
};

export const _mapStateToProps = (state) => ({
    currentPlace: state.location.currentPlace,
    h1Prefix: state.location.h1Prefix,
    cityBlurb: state.location.cityBlurb,
    placeId: state.location.placeId,
    locationSuggestions: getLocationSuggestions(
        state.location,
        state.app.featureFlags.useCurrentLocation,
    ),
    shouldHideMask: getMaskState(state.brand, state.location.isOnline),
    domain: state.app.domain,
    featureFlags: state.app.featureFlags,
    experiments: state.app.experiments,
    locationDenied: state.location.locationDenied,
    isWaitingForLocation: state.location.isWaitingForLocation,
    isOnline: state.location.isOnline,
    isBotRequest: state.app.isBotRequest,
    trackGAEvent: (action) =>
        trackEventFromState(state, {
            category: GA_CITY_BROWSE_CATEGORY,
            ...action,
        }),
    ...state.brand,
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    handleLocationSuggestionChange: (...args) =>
        dispatch(searchLocationSuggestionsAction(...args)),
    handleLocationSelect: (locationChangeObj = {}) =>
        dispatch(
            locationChangeAction({
                ...locationChangeObj,
                history: ownProps.history,
            }),
        ),
    onTrackPageViewAction: (...args) => dispatch(trackPageViewAction(...args)),
    handleUseCurrentLocation: (...args) =>
        dispatch(getUserCurrentLocationAction(...args)),
});

export const CityBrowseBaseScreenContainer = withOverlayControls(
    withMainControls(
        withRouter(
            connect(
                _mapStateToProps,
                _mapDispatchToProps,
            )(CityBrowseBaseScreen),
        ),
    ),
);
